// src/redux/slices/blogSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Base URL for API
const BASE_URL = 'https://evercorenutrition.com/ev_admin/api';

// Async thunk for fetching blogs
export const fetchBlogs = createAsyncThunk(
  'blogs/fetchBlogs',
  async ({ page = 1, pageSize = 9 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/blogs/?page=${page}&page_size=${pageSize}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for fetching single blog by ID
export const fetchBlogById = createAsyncThunk(
  'blogs/fetchBlogById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/blogs/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for fetching blogs by tag
export const fetchBlogsByTag = createAsyncThunk(
  'blogs/fetchBlogsByTag',
  async (tag, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/blogs/?tag=${encodeURIComponent(tag)}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  blogs: [],
  currentBlog: null,
  totalCount: 0,
  totalPages: 1,
  currentPage: 1,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  tags: [],
};

// Create slice
const blogSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {
    clearCurrentBlog: (state) => {
      state.currentBlog = null;
    },
    clearBlogs: (state) => {
      state.blogs = [];
      state.totalCount = 0;
      state.totalPages = 1;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    extractTags: (state) => {
      // Extract unique tags from all blogs
      const allTags = new Set();
      state.blogs.forEach(blog => {
        if (blog.tags) {
          blog.tags.split(',').forEach(tag => allTags.add(tag.trim()));
        }
      });
      state.tags = Array.from(allTags);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch blogs cases
      .addCase(fetchBlogs.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.blogs = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
        state.totalPages = action.payload.total_pages || 1;
        state.currentPage = action.payload.current_page || 1;
        
        // Extract tags from fetched blogs
        const allTags = new Set();
        state.blogs.forEach(blog => {
          if (blog.tags) {
            blog.tags.split(',').forEach(tag => allTags.add(tag.trim()));
          }
        });
        state.tags = Array.from(allTags);
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch blogs';
      })

      // Fetch single blog cases
      .addCase(fetchBlogById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBlogById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentBlog = action.payload;
      })
      .addCase(fetchBlogById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch blog';
      })

      // Fetch blogs by tag cases
      .addCase(fetchBlogsByTag.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBlogsByTag.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.blogs = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
      })
      .addCase(fetchBlogsByTag.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch blogs by tag';
      });
  },
});

// Export actions
export const { clearCurrentBlog, clearBlogs, setCurrentPage, extractTags } = blogSlice.actions;

// Export selectors
export const selectAllBlogs = (state) => state.blogs.blogs;
export const selectCurrentBlog = (state) => state.blogs.currentBlog;
export const selectBlogsStatus = (state) => state.blogs.status;
export const selectBlogsError = (state) => state.blogs.error;
export const selectBlogsTotalCount = (state) => state.blogs.totalCount;
export const selectBlogsTotalPages = (state) => state.blogs.totalPages;
export const selectBlogsCurrentPage = (state) => state.blogs.currentPage;
export const selectAllTags = (state) => state.blogs.tags;

// Export reducer
export default blogSlice.reducer;